import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const GroupContext = createContext(null);

export function GroupProvider({ children }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const [group, setGroup] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadGroup = useCallback(async () => {
    if (!slug || !user) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/groups/by-slug/${slug}`);
      setGroup(data);
      localStorage.setItem("activeGroupSlug", data.slug);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setError("not_found");
      else if (status === 403) setError("forbidden");
      else setError("unknown");
      setGroup(null);
    } finally {
      setLoading(false);
    }
  }, [slug, user]);

  const loadMyGroups = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/groups");
      setMyGroups(data);
    } catch (_) {}
  }, [user]);

  useEffect(() => { loadGroup(); }, [loadGroup]);
  useEffect(() => { loadMyGroups(); }, [loadMyGroups]);

  const refresh = () => { loadGroup(); loadMyGroups(); };

  const value = {
    slug,
    group,
    groupId: group?.id,
    isAdmin: !!group?.is_admin,
    members: group?.members || [],
    myGroups,
    loading,
    error,
    refresh,
  };

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
}

export function useGroup() {
  const ctx = useContext(GroupContext);
  if (!ctx) throw new Error("useGroup must be inside GroupProvider");
  return ctx;
}
